import type { FC } from 'react';

import { normalizeSectionTheme } from '#/lib/section-theme';
import { cn } from '#/lib/utils';

/**
 * Decorative hero background graphics ("Website 2026" Figma `Backgrounds`
 * component), exported from Figma as flattened 2x WebP rasters. Raster (not
 * SVG) is deliberate: the graphics rely on multiply/overlay blend modes
 * inside opacity groups, which Figma composites non-isolated but browsers
 * isolate per SVG/CSS compositing rules — an SVG export renders visibly
 * wrong. Each asset includes its own base fill and left-edge fade, so it can
 * simply cover the whole section.
 *
 * Graphics are viewport-edge anchored (not the content container). Default is
 * top-right; Rails Landing uses top-left; the home hero overrides with bottom
 * anchoring (left-bottom on mobile).
 */

export type HeroBackground = {
  src: string;
  /**
   * Base colour of the graphic (the flat fill at its faded edges, sampled
   * from the Figma variant). Applied to the section so the surface matches
   * before the image loads and behind overscroll — some variants sit on a
   * different base than the standard theme surface.
   */
  baseColor: string;
  /**
   * Optional object-position / crop overrides. Most graphics anchor to the
   * top-right; Rails Landing is composed for top-left.
   */
  className?: string;
};

/** Always-on background for the home page hero — not CMS-selectable. */
export const HOME_HERO_BACKGROUND: HeroBackground = {
  src: '/backgrounds/hero-home.webp',
  baseColor: '#1C1C1E',
};

/**
 * Rails Form Landing graphic. Also CMS-selectable on charcoal/dark heroes
 * (`rails-landing` in BackgroundInput) so any hero can sit on a Rails page.
 */
export const RAILS_LANDING_HERO_BACKGROUND: HeroBackground = {
  src: '/backgrounds/hero-rails-landing.webp',
  baseColor: '#1C1C1E',
  className: 'object-left-top',
};

const LIGHT_GRAPHICS: Record<string, HeroBackground> = {
  'graphic-1': { src: '/backgrounds/hero-light-1.webp', baseColor: '#F4F4F5' },
};

const CHARCOAL_GRAPHICS: Record<string, HeroBackground> = {
  'graphic-1': {
    src: '/backgrounds/hero-charcoal-1.webp',
    baseColor: '#1C1C1E',
  },
  'graphic-2': {
    src: '/backgrounds/hero-charcoal-2.webp',
    baseColor: '#1C1C1E',
  },
  'rails-landing': RAILS_LANDING_HERO_BACKGROUND,
};

const THEME_GRAPHICS: Record<string, Record<string, HeroBackground>> = {
  purple: {
    'graphic-1': {
      src: '/backgrounds/hero-purple-1.webp',
      baseColor: '#5F5BF7',
    },
    'graphic-2': {
      src: '/backgrounds/hero-purple-2.webp',
      baseColor: '#34336C',
    },
    'graphic-3': {
      src: '/backgrounds/hero-purple-3.webp',
      baseColor: '#19176C',
    },
  },
  charcoal: CHARCOAL_GRAPHICS,
  light: LIGHT_GRAPHICS,
  white: LIGHT_GRAPHICS,
};

export function getHeroBackground(
  theme: string | null | undefined,
  pattern: string | null | undefined,
): HeroBackground | undefined {
  if (!pattern || pattern === 'none') return undefined;
  return THEME_GRAPHICS[normalizeSectionTheme(theme)]?.[pattern];
}

/**
 * Renders inside a `relative isolate` section, behind its content. The
 * section's theme surface class stays on the wrapper so the correct base
 * colour paints before the image loads. Pass `className` to override the
 * default bottom-right anchoring.
 */
export const HeroBackgroundGraphic: FC<{
  background: HeroBackground;
  className?: string;
}> = ({ background, className }) => (
  // eslint-disable-next-line @next/next/no-img-element -- pre-optimized static WebP; no next/image resizing needed
  <img
    src={background.src}
    alt=""
    aria-hidden
    draggable={false}
    className={cn(
      'pointer-events-none absolute inset-0 -z-10 h-full w-full select-none object-cover object-right-top',
      background.className,
      className,
    )}
  />
);
